"use client";

import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";

import { BindableInput } from "./BindableInput";
import { Combobox } from "@/components/shadcn/combobox";
import { Button } from "@/components/shadcn/button";
import { Banner } from "@/components/Banner";
import { NoxusFormHelperText } from "@/components/Form/NoxusFormHelperText";
import { cn } from "@/lib/utils";

/**
 * V2 renderer for `display=ConfigBindableDictSelectRows`. A map where each row
 * is a column picked from a dropdown (options from `values`, each column
 * pickable once) plus a bindable value (literal or `:var[...]` ref, picker
 * inherited from {@link BindableInput}). Stored as `{column: value}` — the same
 * shape as ConfigBindableDictRows, so it's the bindable equivalent of the V1
 * `ConfigDictSelectArray` mapping. `values` (the column options) are populated
 * dynamically by the node's `get_config`.
 */
export function BindableDictSelectRowsField({
  nodeId,
  fieldKey,
  values,
  label,
  description,
  placeholder,
  addLabel,
  dataType,
  disabled,
  error,
}: {
  nodeId: string;
  fieldKey: string;
  values?: unknown;
  label?: string;
  description?: string;
  placeholder?: string;
  addLabel?: string;
  dataType?: string;
  disabled?: boolean;
  error?: string;
}) {
  const form = useFormContext();
  const path = `${nodeId}.${fieldKey}`;
  const raw = form.watch(path) as Record<string, unknown> | undefined;
  const rows: Record<string, unknown> = useMemo(
    () => (raw && typeof raw === "object" ? raw : {}),
    [raw]
  );
  const columns: string[] = useMemo(
    () =>
      Array.isArray(values)
        ? values.filter((v): v is string => typeof v === "string")
        : [],
    [values]
  );
  const usedKeys = useMemo(() => new Set(Object.keys(rows)), [rows]);
  const invalid = !!error;

  const write = (next: Record<string, unknown>) =>
    form.setValue(path, next, { shouldDirty: true, shouldTouch: true });

  const setRow = (name: string, next: unknown) =>
    write({ ...rows, [name]: next });

  const renameRow = (oldName: string, newName: string) => {
    if (!newName || newName === oldName || rows[newName] !== undefined) return;
    const next: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(rows))
      next[k === oldName ? newName : k] = v;
    write(next);
  };

  const removeRow = (name: string) => {
    const next = { ...rows };
    delete next[name];
    write(next);
  };

  const nextUnusedColumn = columns.find((c) => !usedKeys.has(c));

  const addRow = () => {
    if (!nextUnusedColumn) return;
    write({ ...rows, [nextUnusedColumn]: "" });
  };

  // Per row, offer columns not used by other rows (keeping this row's own key)
  // so each column can be mapped at most once.
  // A saved key missing from the loaded columns (renamed header, options still
  // loading) stays listed so the row doesn't look unmapped.
  const optionsFor = (currentKey: string) => [
    ...(currentKey && !columns.includes(currentKey)
      ? [{ value: currentKey, label: currentKey }]
      : []),
    ...columns
      .filter((c) => c === currentKey || !usedKeys.has(c))
      .map((c) => ({ value: c, label: c })),
  ];

  return (
    <div className="flex flex-col gap-2">
      {description ? (
        <NoxusFormHelperText>{description}</NoxusFormHelperText>
      ) : null}
      <div
        className={cn(
          "flex w-full flex-col gap-4",
          invalid && "rounded-md ring-1 ring-inset ring-amber-400/70 p-1.5"
        )}
      >
        {Object.keys(rows).map((key) => (
          <div key={key} className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <Combobox
                options={optionsFor(key)}
                value={key || undefined}
                placeholder={
                  label
                    ? `Select ${label.toLowerCase()} column…`
                    : "Select column…"
                }
                searchPlaceholder="Search columns…"
                emptyText="No columns available"
                onValueChange={(v) => {
                  if (v) renameRow(key, v);
                }}
                disabled={disabled}
                hasPortal
                className="h-8 min-w-0 flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={disabled}
                aria-label="Remove"
                className="size-8 shrink-0 text-muted-foreground"
                onClick={() => removeRow(key)}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
            <BindableInput
              nodeId={nodeId}
              dataType={dataType ?? "str"}
              value={typeof rows[key] === "string" ? (rows[key] as string) : ""}
              placeholder={placeholder}
              disabled={disabled}
              onChange={(next) => setRow(key, next)}
            />
          </div>
        ))}
      </div>
      {columns.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          Select a sheet to load its columns.
        </p>
      ) : null}
      <Button
        type="button"
        variant="outline"
        disabled={disabled || !nextUnusedColumn}
        className="w-full"
        onClick={addRow}
      >
        {addLabel ?? "Add column"}
        <Plus size="1.25rem" strokeWidth="1.25px" />
      </Button>
      {error ? <Banner label={error} style="warning" size="sm" /> : null}
    </div>
  );
}
